import { Loading } from "react-migration/components/Loading";
import { Alert } from "react-migration/components/Alert";
import { useTitleOwnerQuery } from "react-migration/domains/ownership/apollo/useTitleOwnerQuery";
import { TitleOwner } from "react-migration/domains/ownership/components/TitleOwner";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { SelectionFeature } from "src/js/stores/map/store";
import { showError } from "src/js/stores/user/actions/addNotification";
import { DataPanel } from "react-migration/components/DataPanel";
import { getTitleOwners } from "react-migration/domains/ownership/helpers/getTitleOwners";
import { Button } from "react-migration/components/Button";

interface USOwnershipDetailPageProps {
  selection: SelectionFeature;
}

export function USOwnershipDetailPage({ selection }: USOwnershipDetailPageProps) {
  const { t } = useTranslation();
  const { titleOwner: title, loading: titleLoading, error: titleError } = useTitleOwnerQuery(selection.id);

  if (titleError) {
    console.error(titleError);
    showError({
      id: `us-ownership-detail-page-error`,
      title: "Error retrieving ownership details",
      description: "An error occurred loading the ownership details. Please try again later.",
      duration: 2000,
    });
  }

  if (titleLoading)
    return (
      <div className="mt-2 mb-6">
        <Loading />
      </div>
    );

  if (!title) {
    return (
      <div className="m-2">
        <Alert.Info>{t("ownership.react.map.sidebar.property_info.main_plot_not_found")}</Alert.Info>
      </div>
    );
  }

  const getAppraiserLink = () => {
    for (const owner of getTitleOwners(title)) {
      if (owner.appraiser_link) return owner.appraiser_link;
    }
  };

  return (
    <div className="flex flex-col gap-y-1">
      <DataPanel.Grid columns={1} className="border-b border-border-base-divider pb-1">
        <DataPanel.Cell
          variant="ghost"
          title={t("ownership.react.map.sidebar.property_info.address_title")}
          data-testid="assessment-ownership-address"
        >
          <div className="flex flex-col gap-2">
            <div>
              {title.addresses?.length
                ? String(title.addresses[0]).trim()
                : t("ownership.react.map.sidebar.property_info.value_unavailable")}
            </div>
            {getAppraiserLink() && (
              <div>
                <Button
                  variant="tertiary"
                  onClick={() => window.open(getAppraiserLink(), "_blank")}
                  tailIcon="lt-external-link-line"
                >
                  {t("ownership.react.map.sidebar.title_owner.ultimate_owner.appraiser_link")}
                </Button>
              </div>
            )}
          </div>
        </DataPanel.Cell>
      </DataPanel.Grid>
      <TitleOwner mainTitle={title} />
    </div>
  );
}
